/**
 * Route Prefetch Service
 * 
 * SECURITY-AWARE, DEBOUNCED ROUTE PRE-FETCHING ENGINE
 * 
 * This service provides a security-gated mechanism to pre-load lazy-loaded
 * route chunks on hover, ensuring near-zero navigation latency for
 * authenticated users.
 * 
 * ARCHITECTURE (100% Dynamic — Zero Hardcoding):
 * - Uses a DYNAMIC RUNTIME REGISTRY pattern. Routes self-register their
 *   import factories via `registerPrefetchRoute()` at definition time in App.tsx.
 * - The registry is populated from the same `React.lazy()` factory calls that
 *   already exist, ensuring zero duplication and single-source-of-truth compliance.
 * - Route paths come from `APP_PATHS` (appRoutes.ts), not hardcoded strings.
 * 
 * SECURITY DESIGN:
 * - Pre-fetching ONLY downloads the compiled JS chunk. It does NOT trigger
 *   any API calls, data fetches, or backend interactions.
 * - Even if an attacker downloads a chunk, no data is exposed — all data
 *   access is gated by backend auth middleware (Rule 13: Siege Defense).
 * - The prefetch registry is session-scoped (in-memory Set), preventing
 *   duplicate network requests and resource exhaustion.
 * 
 * PERFORMANCE DESIGN:
 * - Debounced hover gate (configurable delay) prevents accidental sweeps
 *   from flooding the network with chunk requests.
 * - Registry tracks already-prefetched paths to guarantee at-most-once
 *   fetch per route per session.
 */

// ============================================================================
// CONFIGURATION CONSTANTS
// ============================================================================

/** Minimum hover duration (ms) before triggering a prefetch. Prevents accidental sweeps. */
const PREFETCH_DEBOUNCE_MS = 80;

/** Maximum number of routes that can be prefetched in a single session to prevent abuse. */
const MAX_PREFETCHED_ROUTES = 50;

// ============================================================================
// DYNAMIC ROUTE FACTORY REGISTRY (Runtime-Populated)
// ============================================================================

/**
 * Dynamic registry mapping route paths to their import factory functions.
 * Populated at module initialization time by `registerPrefetchRoute()` calls
 * in App.tsx, ensuring the registry is always in sync with the actual route
 * definitions without any hardcoded duplication.
 */
const routeFactoryRegistry = new Map<string, () => Promise<unknown>>();

/**
 * Registers a route's dynamic import factory for hover-based prefetching.
 * Called from App.tsx alongside `React.lazy()` to ensure single-source-of-truth.
 * 
 * @param path - The route path from APP_PATHS (e.g., APP_PATHS.ADMIN.DASHBOARD)
 * @param factory - The same dynamic import factory used by React.lazy()
 * 
 * @example
 * ```ts
 * // In App.tsx — factory is defined once, used for both lazy loading AND prefetching
 * const dashboardFactory = () => import('./pages/admin/AdminDashboardPage');
 * registerPrefetchRoute(APP_PATHS.ADMIN.DASHBOARD, dashboardFactory);
 * const AdminDashboardPage = React.lazy(dashboardFactory);
 * ```
 */
export function registerPrefetchRoute(
  path: string,
  factory: () => Promise<unknown>
): void {
  // Skip parameterized routes (e.g., '/admin/users/:roleKey') — these can't be prefetched
  if (path.includes(':')) return;
  routeFactoryRegistry.set(path, factory);
}

/**
 * Batch-registers multiple routes at once for cleaner App.tsx integration.
 * 
 * @param routes - Array of [path, factory] tuples
 */
export function registerPrefetchRoutes(
  routes: ReadonlyArray<readonly [string, () => Promise<unknown>]>
): void {
  for (const [path, factory] of routes) {
    registerPrefetchRoute(path, factory);
  }
}

// ============================================================================
// PREFETCH STATE TRACKING (Session-Scoped Singletons)
// ============================================================================

/** Tracks which route paths have already been prefetched this session. */
const prefetchedRegistry = new Set<string>();

/** Tracks active debounce timers to enable cleanup on mouse-leave. */
const activeTimers = new Map<string, ReturnType<typeof setTimeout>>();

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Creates a debounced prefetch handler for a specific route.
 * Returns `onMouseEnter` and `onMouseLeave` handlers to attach to navigation elements.
 * 
 * @param routePath - The route path identifier (used as registry key)
 * @param factory - The dynamic import factory function
 * @returns Object with `onMouseEnter` and `onMouseLeave` event handlers
 */
export function createPrefetchHandlers(
  routePath: string,
  factory: () => Promise<unknown>
): { onMouseEnter: () => void; onMouseLeave: () => void } {
  const onMouseEnter = (): void => {
    // Guard 1: Already prefetched — skip
    if (prefetchedRegistry.has(routePath)) return;

    // Guard 2: Session ceiling reached — prevent resource exhaustion
    if (prefetchedRegistry.size >= MAX_PREFETCHED_ROUTES) return;

    // Guard 3: Timer already active for this path — skip (prevents duplicate timers)
    if (activeTimers.has(routePath)) return;

    // Debounced execution: only fires if hover persists past the threshold
    const timer = setTimeout(() => {
      activeTimers.delete(routePath);

      // Double-check registry (race condition guard)
      if (prefetchedRegistry.has(routePath)) return;

      // Execute the factory to trigger Webpack/Vite chunk download.
      // The promise result is intentionally discarded — we only need the side-effect
      // of the browser caching the chunk.
      factory().then(() => {
        prefetchedRegistry.add(routePath);
      }).catch(() => {
        // Silently swallow prefetch failures — this is a performance optimization,
        // not a critical path. The chunk will load normally on navigation.
      });
    }, PREFETCH_DEBOUNCE_MS);

    activeTimers.set(routePath, timer);
  };

  const onMouseLeave = (): void => {
    // Cancel the debounce timer if the user moves away before the threshold
    const timer = activeTimers.get(routePath);
    if (timer) {
      clearTimeout(timer);
      activeTimers.delete(routePath);
    }
  };

  return { onMouseEnter, onMouseLeave };
}

/**
 * Resolves the prefetch factory for a given navigation path from the dynamic registry.
 * Uses exact match first, then prefix match for nested/parameterized routes.
 * 
 * @param path - The navigation link path
 * @returns The factory function if found, undefined otherwise
 */
export function resolvePrefetchFactory(path: string): (() => Promise<unknown>) | undefined {
  // Exact match first (fastest path)
  const exactMatch = routeFactoryRegistry.get(path);
  if (exactMatch) return exactMatch;

  // Prefix match for nested routes (e.g., '/admin/users/create' → '/admin/users')
  for (const [key, factory] of routeFactoryRegistry.entries()) {
    if (path.startsWith(key + '/')) {
      return factory;
    }
  }

  return undefined;
}
